/** Banco local dedicado exclusivamente aos rascunhos dos formulários abertos. */
import { environmentStorageKey } from "@/lib/appEnvironment"
import Dexie, { type Table } from "dexie"
import type { FieldValues } from "react-hook-form"
import type { FormModel, SubmissionDto } from "@/types/forms-api"
import type { PendingSubmission } from "@/stores/useSubmissionQueueStore"

export interface FormDraftRow {
  key: string
  data: FieldValues
  updatedAt: number
  userId?: string
  workspaceId?: string
  submissionId?: string
  taskId?: string
  taskName?: string
  resourceId?: string | null
  scheduleId?: string | null
  scheduleSnapshot?: import("@/types/sync-api").TaskScheduleSyncItemDto
  form?: FormModel
  formVersionId?: string
  revision?: string
  syncedRevision?: string
  serverUpdatedAt?: string
  cosmosAnswerDocId?: string | null
  status?: "editing" | "paused" | "queued" | "completed" | "conflict"
  startedAt?: string
  startedLatitude?: number | null
  startedLongitude?: number | null
  editingSessionId?: string
  completionRequestedAt?: string
  completedLatitude?: number | null
  completedLongitude?: number | null
  previewDismissed?: boolean
  acknowledgedSummary?: SubmissionDto
  cleanupMediaIds?: string[]
  deletionConfirmed?: boolean
  deletionAttempted?: boolean
}

export interface DraftSessionRow {
  key: string
  sessionId: string
  userId: string
  workspaceId: string
  submissionId: string
  baseline: FormDraftRow | null
  suspendedPause?: PendingSubmission
  phase: "editing" | "discarding" | "discarded" | "committed"
  addedMediaIds?: string[]
}

export interface DraftSyncRow {
  key: string
  timestamp: number
}

class FormDraftDb extends Dexie {
  drafts!: Table<FormDraftRow, string>
  sync!: Table<DraftSyncRow, string>
  sessions!: Table<DraftSessionRow, string>

  constructor() {
    // Nome legado mantido para migrar rascunhos existentes sem perda de dados.
    super(environmentStorageKey("dkro-offline-db"))
    this.version(1).stores({ tasks: "key, workspaceId", forms: "key, workspaceId", drafts: "key" })
    this.version(2).stores({ lists: "key, workspaceId" })
    // Limpa os caches legados na migração, preservando os rascunhos.
    this.version(3).stores({ tasks: null, forms: null, lists: null, drafts: "key, updatedAt" })
    this.version(4).stores({ drafts: "key, updatedAt, [userId+workspaceId], submissionId", sync: "key" })
    this.version(5).stores({ sessions: "key, [userId+workspaceId]" })
    this.version(6).stores({}).upgrade(async transaction => {
      const migrate = (row: FormDraftRow) => {
        if (row.scheduleSnapshot) row.scheduleSnapshot = { ...row.scheduleSnapshot,
          name: (row.scheduleSnapshot.description ?? "").slice(0, 240), description: null }
      }
      await transaction.table<FormDraftRow>("drafts").toCollection().modify(migrate)
      await transaction.table<DraftSessionRow>("sessions").toCollection().modify(row => {
        if (row.baseline) migrate(row.baseline)
      })
    })
  }
}

export const formDraftDb = new FormDraftDb()
